// src/components/CheckOutModal.js
import React, { useState, useRef, useEffect } from 'react';
import { FiX, FiPaperclip, FiTrash2, FiFileText, FiAlertCircle, FiPlus, FiPackage } from 'react-icons/fi';
import { useModal } from '../context/ModalContext';
import ModalSelectInput from './ModalSelectInput';

const CheckOutModal = ({ isOpen, onClose, onCheckOut, isLastTechnician, isImageRequired, truckInventory = [] }) => {
    const [resolutionNote, setResolutionNote] = useState('');
    const [newStatus, setNewStatus] = useState('Closed');
    const [files, setFiles] = useState([]);
    
    // Inventory State
    const [usedItems, setUsedItems] = useState([]); 
    const [selectedItemId, setSelectedItemId] = useState('');
    const [itemQuantity, setItemQuantity] = useState(1);

    const fileInputRef = useRef(null);
    const { showAlert } = useModal(); // ✅ Custom Web Modal

    // Cleanup object URLs to prevent memory leaks
    useEffect(() => {
        const filesForCleanup = [...files];
        return () => {
            filesForCleanup.forEach(file => {
                if (file.preview) {
                    URL.revokeObjectURL(file.preview);
                }
            });
        };
    }, [files]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!isOpen) {
            setResolutionNote('');
            setNewStatus('Closed');
            setFiles([]);
            setUsedItems([]);
            setSelectedItemId('');
            setItemQuantity(1);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFileChange = (e) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files).map(file => Object.assign(file, {
                preview: URL.createObjectURL(file)
            }));
            setFiles(prev => [...prev, ...newFiles]);
        }
    };

    const removeFile = (fileToRemove) => {
        setFiles(prev => {
            const newFiles = prev.filter(file => file !== fileToRemove);
            if (fileToRemove.preview) {
                URL.revokeObjectURL(fileToRemove.preview);
            }
            return newFiles;
        });
    };

    // Inventory Handlers
    const handleAddItem = () => {
        if (!selectedItemId || itemQuantity < 1) return;
        const item = truckInventory.find(i => i._id === selectedItemId);
        if (!item) return;

        setUsedItems(prev => [...prev, {
            inventoryId: item._id,
            name: item.name,
            quantity: Number(itemQuantity)
        }]);
        
        setSelectedItemId('');
        setItemQuantity(1);
    };

    const handleRemoveItem = (index) => {
        setUsedItems(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!resolutionNote.trim()) {
            showAlert('Please provide a resolution note.', 'Validation Error');
            return;
        }

        if (isImageRequired === true && files.length === 0) {
            showAlert('A PHOTO/VIDEO IS REQUIRED. Please attach a file before checking out.', 'Validation Error');
            return;
        }

        if (onCheckOut) {
            onCheckOut({
                resolutionNote,
                newStatus: isLastTechnician ? newStatus : 'In Progress',
                files,
                inventoryUsed: usedItems
            });
            onClose();
        }
    };

    const filePreviews = files.map((file, index) => {
        const isImage = file.type.startsWith('image/');
        return (
            <div key={`${file.name}-${index}`} className="file-preview-item">
                {isImage ? (
                    <img src={file.preview} alt={file.name} className="file-preview-image" />
                ) : (
                    <div className="file-preview-placeholder"><FiFileText size={24}/></div>
                )}
                <span className="file-preview-name">{file.name}</span>
                <button type="button" onClick={() => removeFile(file)} className="btn-icon-only btn-icon-danger"><FiTrash2/></button>
            </div>
        );
    });

    const isSubmitDisabled = !resolutionNote.trim() || (isImageRequired && files.length === 0);
    const availableInventory = truckInventory.filter(item => item.quantity > 0);

    return (
        <div className="modal-overlay">
            <div 
                className="modal-content" 
                onClick={e => e.stopPropagation()}
                style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    maxHeight: '90dvh', 
                    height: 'auto',
                    width: '95%', 
                    maxWidth: '550px' 
                }}
            >
                <div className="modal-header" style={{ flexShrink: 0 }}>
                    <h3 className="modal-title">Check-Out of Ticket</h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>

                <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', overflow: 'hidden', minHeight: 0 }}>
                    <div className="modal-body" style={{ overflowY: 'auto', flex: '1 1 auto' }}>
                        
                        {isImageRequired && (
                            <div style={{ backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.75rem', borderRadius: '6px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', border: '1px solid #fca5a5', fontWeight: 'bold' }}>
                                <FiAlertCircle size={20} />
                                <span>PHOTO/VIDEO REQUIRED</span>
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="resolution-note">Resolution Note</label>
                            <textarea
                                id="resolution-note"
                                className="form-textarea"
                                style={{ minHeight: '100px', resize: 'none' }}
                                value={resolutionNote}
                                onChange={(e) => setResolutionNote(e.target.value)}
                                placeholder="Describe the work performed..."
                                required
                                autoFocus
                            />
                        </div>

                        {/* ✅ INVENTORY SECTION */}
                        <div className="form-group used-inventory-section" style={{ backgroundColor: 'var(--gray-50)', padding: '1rem', borderRadius: '8px', border: '1px solid var(--gray-200)' }}>
                            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Used Parts (From Your Truck)</label>
                            
                            <div className="inventory-select-row" style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                {/* ✅ Replace <select> with ModalSelectInput */}
                                <div style={{ flex: 1 }}>
                                    <ModalSelectInput 
                                        name="selectedItemId"
                                        label="Select Part"
                                        options={[
                                            { label: '-- Select Part --', value: '' },
                                            ...availableInventory.map(item => ({ label: `${item.name} (Qty: ${item.quantity})`, value: item._id }))
                                        ]}
                                        value={selectedItemId}
                                        onChange={e => setSelectedItemId(e.target.value)}
                                        className="form-select"
                                    />
                                </div>
                                <input 
                                    type="number" 
                                    className="form-input" 
                                    style={{ width: '80px' }}
                                    min="1" 
                                    value={itemQuantity}
                                    onChange={e => setItemQuantity(Number(e.target.value))}
                                />
                                <button type="button" className="btn btn-secondary" onClick={handleAddItem} disabled={!selectedItemId}>
                                    <FiPlus />
                                </button>
                            </div>

                            {/* List of added items */}
                            {usedItems.length > 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    {usedItems.map((item, idx) => (
                                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'white', padding: '0.5rem', borderRadius: '4px', border: '1px solid var(--gray-200)', fontSize: '0.9rem' }}>
                                            <span><FiPackage style={{ marginRight: '6px' }}/> {item.quantity}x {item.name}</span>
                                            <button type="button" onClick={() => handleRemoveItem(idx)} className="btn-icon-only btn-icon-danger" style={{ padding: '2px' }}>
                                                <FiX size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="form-group">
                            <label>
                                Attachments {isImageRequired && <span style={{color: 'var(--red-600)'}}>*</span>}
                            </label>
                            <div className="file-upload-area" style={isImageRequired && files.length === 0 ? { borderColor: 'var(--red-500)', backgroundColor: '#fff5f5' } : {}}>
                                {files.length > 0 && (
                                    <div className="file-preview-container" style={{ maxHeight: '150px', overflowY: 'auto', marginBottom: '1rem', justifyContent: 'flex-start' }}>
                                        {filePreviews}
                                    </div>
                                )}
                                <button type="button" className="btn btn-secondary" onClick={() => fileInputRef.current.click()}>
                                    <FiPaperclip className="btn-icon" />
                                    {files.length > 0 ? 'Add More Files' : 'Attach Files / Photos / Videos'}
                                </button>
                                <input type="file" ref={fileInputRef} multiple onChange={handleFileChange} style={{ display: 'none' }} accept="image/*,video/*,application/pdf" />
                            </div>
                        </div>
                        
                        {isLastTechnician && (
                            <div className="form-group">
                                <label htmlFor="new-status">Set Final Status</label>
                                {/* ✅ Replace <select> with ModalSelectInput */}
                                <ModalSelectInput 
                                    name="newStatus"
                                    label="Final Status"
                                    options={[
                                        { label: 'Closed', value: 'Closed' },
                                        { label: 'Pending', value: 'Pending' },
                                        { label: 'Waiting On Client', value: 'Waiting On Client' },
                                        { label: 'Waiting On Parts', value: 'Waiting On Parts' }
                                    ]}
                                    value={newStatus}
                                    onChange={(e) => setNewStatus(e.target.value)}
                                    className="form-select"
                                />
                            </div>
                        )}
                    </div>

                    <div className="modal-footer" style={{ flexShrink: 0, flexDirection: 'column', gap: '1rem', alignItems: 'stretch' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
                            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
                            <button type="submit" className="btn btn-primary" disabled={isSubmitDisabled} style={isSubmitDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}>
                                Confirm Check-Out
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CheckOutModal;